// Shared by the screen and the Excel export so column order and colors stay aligned.
export const reconciliationColumns = [
  { key: "fecha", accessor: "dia", label: "Día", width: "6%" },
  { key: "peaje", accessor: "peaje", label: "Peajes", width: "12%" },
  { key: "totalPasos", accessor: "totalPasos", label: "Total pasos", width: "9%" },
  {
    key: "gopassPasosExitosos",
    accessor: "gopassPasosExitosos",
    label: "Gopass Pasos Exitosos",
    width: "12%",
  },
  { key: "valorConcesion", accessor: "concesion", label: "Valor concesión", width: "10%" },
  {
    key: "diferenciaInicial",
    accessor: "diferenciaInicial",
    label: "Diferencia Inicial",
    width: "10%",
  },
  { key: "pagosRechazos", accessor: "pagosRechazos", label: "Pagos Rechazos", width: "10%" },
  { key: "totalGopass", accessor: "totalGopass", label: "Total Gopass", width: "10%" },
  { key: "diferenciaFinal", accessor: "diferenciaFinal", label: "Diferencia Final", width: "10%" },
  { key: "pagoFinanciera", accessor: "pagoFinanciera", label: "Pago financiera", width: "10%" },
  {
    key: "diferenciaFinanciera",
    accessor: "diferenciaFinanciera",
    label: "Diferencia Financiera",
    width: "10%",
  },
  {
    key: "valoresAdicionales",
    accessor: "valoresAdicionales",
    label: "Valores Adicionales",
    width: "10%",
  },
  { key: "descuentos", accessor: "descuentos", label: "Descuentos", width: "8%" },
  { key: "observacion", accessor: "observacion", label: "Observación", width: "18%" },
];
const distributionColumns = (source, color) =>
  [
    ["via40", "VÍA 40 1"],
    ["alosur", "VÍA ALÓSUR"],
    ["fosevi", "FOSEVI"],
  ].map(([prefix, label]) => ({
    key: `${prefix}${source}`,
    accessor: `${prefix}${source}`,
    label,
    width: "10%",
    color,
  }));
export const getReconciliationColumns = (via40) => {
  if (!via40) return reconciliationColumns;
  const column = (key, color) => ({
    ...reconciliationColumns.find((item) => item.key === key),
    ...(color ? { color } : {}),
  });
  const ordered = [
    column("fecha"),
    column("peaje"),
    column("totalPasos", "E2EFDA"),
    column("gopassPasosExitosos", "E2EFDA"),
    ...distributionColumns("Gopass", "E2EFDA"),
    column("diferenciaInicial", "E2EFDA"),
    column("valorConcesion", "FCE4D6"),
    ...distributionColumns("Concesion", "FCE4D6"),
    column("diferenciaFinal", "FCE4D6"),
  ];
  const included = new Set(ordered.map(({ key }) => key));
  return [...ordered, ...reconciliationColumns.filter(({ key }) => !included.has(key))];
};
export const reconciliationColor = (index, columns = reconciliationColumns) => {
  if (columns[index]?.color) return columns[index].color;
  const originalIndex = reconciliationColumns.findIndex(({ key }) => key === columns[index]?.key);
  return originalIndex <= 0 || originalIndex >= 13
    ? "FFFFFF"
    : originalIndex <= 5
    ? "E2EFDA"
    : originalIndex <= 8
    ? "FFF2CC"
    : "DDEBF7";
};
export const reconciliationBoundary = (key) =>
  ["diferenciaInicial", "diferenciaFinal"].includes(key);
export const reconciliationTableSx = (
  theme,
  columns = reconciliationColumns,
  stickyIdentifiers = true
) => {
  const dark = theme.palette.mode === "dark";
  const date = theme.palette.background.paper;
  const colors = {
    E2EFDA: dark ? "#263d30" : "#E2EFDA",
    FFF2CC: dark ? "#423b23" : "#FFF2CC",
    DDEBF7: dark ? "#263b50" : "#DDEBF7",
    FCE4D6: dark ? "#503526" : "#FCE4D6",
    FFFFFF: date,
  };
  const centered = columns.some(({ key }) => key === "via40Gopass");
  const columnStyles = Object.fromEntries(
    [...columns, { key: "acciones" }].map((column, index) => [
      `& th:nth-of-type(${index + 1}), & td:nth-of-type(${index + 1})`,
      {
        bgcolor: colors[reconciliationColor(index, columns)],
        ...(centered && column.key !== "observacion"
          ? {
              textAlign: "center",
              "& .MuiBox-root, & .MuiTypography-root": { textAlign: "center" },
            }
          : {}),
        ...(stickyIdentifiers && index === 1
          ? { position: "sticky", left: 100, minWidth: 210, zIndex: 2 }
          : {}),
        ...(reconciliationBoundary(column.key)
          ? { borderRight: "2px solid", borderRightColor: dark ? "#607287" : "#9ba8b4" }
          : {}),
        ...(column.key === "observacion"
          ? {
              minWidth: 260,
              maxWidth: 360,
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              textAlign: "left",
            }
          : {}),
        ...(column.key === "acciones" ? { textAlign: "center" } : {}),
      },
    ])
  );
  return {
    maxHeight: 850,
    isolation: "isolate",
    "& th, & td": {
      fontSize: "0.82rem",
      px: 2,
      py: 1.5,
      whiteSpace: "nowrap",
      borderBottom: "1px solid",
      borderColor: "divider",
      color: "text.primary",
    },
    "& thead": { display: "table-header-group" },
    "& th": { fontWeight: 700 },
    "& th:first-of-type, & td:first-of-type": {
      position: stickyIdentifiers ? "sticky" : "static",
      left: 0,
      minWidth: stickyIdentifiers ? 100 : 180,
      width: stickyIdentifiers ? 100 : "auto",
      maxWidth: stickyIdentifiers ? 100 : "none",
      zIndex: 2,
      bgcolor: date,
    },
    "& th:nth-of-type(2), & td:nth-of-type(2)": {
      position: stickyIdentifiers ? "sticky" : "static",
      left: 100,
      minWidth: 210,
      zIndex: 2,
    },
    ...columnStyles,
    "& thead th:first-of-type, & thead th:nth-of-type(2)": { zIndex: 4 },
    "& tbody tr:hover td": { boxShadow: "inset 0 0 0 9999px rgba(0,0,0,0.025)" },
    '& tbody tr:has([data-subtotal="true"]) td': {
      borderBottom: "1px solid",
      borderBottomColor: dark ? "#8494a5" : "#64748b",
      fontWeight: 700,
    },
    "& tfoot td": {
      position: "sticky",
      bottom: 0,
      zIndex: 3,
      borderTop: "2px solid",
      borderTopColor: dark ? "#8494a5" : "#64748b",
    },
    "& tfoot td:first-of-type, & tfoot td:nth-of-type(2)": { zIndex: 4 },
  };
};
